import { useEffect, useRef, useState } from 'react'
import { useJob } from '@/api/endpoints/jobs'
import { readCutSnapshot, useStartCutVideo } from '@/api/endpoints/videoCutter'
import { resolveDownloadUrl } from '@/api/endpoints/youtubeDownload'
import { errorMessage } from '@/lib/errors'
import {
  activeClips,
  clipLabel,
  isFullFrame,
  isNeutralGrade,
  normalizeFileStem,
  orderedFreezes,
  videoById,
  type ProjectVideo,
  type ToolProject,
} from '../model'
import type { ExportState } from '../components/ClipsPanel'

export interface ProjectExportOptions {
  project: ToolProject
  onChange: (updater: (project: ToolProject) => ToolProject) => void
  /** Fallback source for segments that don't name one. Any video of the
   * project will do — the backend only uses it when a segment omits its
   * own path, which this never does. */
  fallbackVideo: ProjectVideo | null
}

export interface ProjectExportApi {
  exportState: ExportState
  runExport: (mode: 'download' | 'merge') => void
}

/**
 * The export half of the editor: fire ``/cut-video``, follow the job, fold
 * the result back into the project.
 *
 * It has nothing to do with editing, which is why it moved out of
 * ``ProjectEditor`` — the only thing it shares with the rest of the page is
 * the project object itself.
 */
export function useProjectExport({
  project,
  onChange,
  fallbackVideo,
}: ProjectExportOptions): ProjectExportApi {
  const startCut = useStartCutVideo()
  const [cutJobId, setCutJobId] = useState<string | null>(null)
  const cutJob = useJob(cutJobId)
  const [merged, setMerged] = useState<ExportState['merged']>(null)
  /** Clip ids in the order they were sent, so ``snapshot.clips[i].index``
   * maps back even if the list is edited while the job runs. */
  const exportOrderRef = useRef<number[]>([])

  // Any change to what would be exported invalidates the joined file —
  // including the ones that don't move a boundary: re-timing a clip or
  // holding a frame changes the output without changing start/end.
  const clipsSignature = project.clips
    .map((c) =>
      [
        c.id,
        c.videoId,
        c.start.toFixed(2),
        c.end.toFixed(2),
        c.hidden ? 'h' : '',
        c.speed.toFixed(2),
        c.freezes.map((f) => `${f.at.toFixed(2)}x${f.duration.toFixed(2)}`).join(','),
      ].join(':'),
    )
    .join('|')
  const cropSignature = project.crop
    ? `${String(project.crop.x)},${String(project.crop.y)},${String(project.crop.width)},${String(project.crop.height)}`
    : ''
  useEffect(() => {
    setMerged(null)
  }, [clipsSignature, cropSignature])

  useEffect(() => {
    if (!cutJobId) return
    const snap = readCutSnapshot(cutJob.data)
    if (!snap) return
    const order = exportOrderRef.current
    if (snap.clips && snap.clips.length > 0) {
      const byClipId = new Map<number, { url: string; filename: string }>()
      for (const result of snap.clips) {
        const clipId = order[result.index]
        if (clipId !== undefined) {
          byClipId.set(clipId, {
            url: resolveDownloadUrl(result.download_url),
            filename: result.suggested_filename,
          })
        }
      }
      onChange((p) => ({
        ...p,
        clips: p.clips.map((c) => {
          const dl = byClipId.get(c.id)
          return dl ? { ...c, download: dl } : c
        }),
      }))
    }
    if (snap.merged) {
      setMerged({
        url: resolveDownloadUrl(snap.merged.download_url),
        filename: snap.merged.suggested_filename,
        duration: snap.merged.duration,
      })
    }
    setCutJobId(null) // consume: stops polling, flips the panel back
  }, [cutJobId, cutJob.data, onChange])

  const runExport = (mode: 'download' | 'merge') => {
    const base = fallbackVideo
    // Hidden clips are not in the cut, so they are not in the export and
    // not in the index map either — the same list `buildTimeline` lays out.
    const clips = activeClips(project)
    if (!base || clips.length === 0 || startCut.isPending || cutJobId) return
    exportOrderRef.current = clips.map((c) => c.id)
    const crop = isFullFrame(project.crop) ? null : project.crop

    startCut.mutate(
      {
        // Every segment carries its own source; this is the fallback the
        // backend uses for segments that don't.
        tempPath: base.tempPath,
        mode: mode === 'merge' ? 'merge' : 'download',
        // The project's framing applies to every cut, in both modes: it is
        // the shape of the output, not a finishing touch on the joined file.
        ...(crop ? { crop } : {}),
        ...(mode === 'merge'
          ? {
              mergeLabel: normalizeFileStem(project.name) || 'clipes',
              // Captions and grades ride along only on the joined video: on
              // a per-clip export one spanning a join has no single clip to
              // belong to. Times are already the joined file's own clock,
              // since both are stored on the clips axis.
              captions: project.texts.map((t) => ({
                text: t.text,
                start: t.start,
                end: t.end,
                x: t.x,
                y: t.y,
                size: t.size,
                font_family: t.fontFamily,
                color: t.color,
                background: t.background,
                shadow_size: t.shadowSize,
                shadow_color: t.shadowColor,
                transition_in: { kind: t.transitionIn.kind, duration: t.transitionIn.duration },
                transition_out: { kind: t.transitionOut.kind, duration: t.transitionOut.duration },
              })),
              grades: project.grades
                // A neutral grade is a filter pass that changes nothing —
                // dropping it here keeps the chain (and the encode) honest.
                .filter((g) => !isNeutralGrade(g))
                .map((g) => ({
                  start: g.start,
                  end: g.end,
                  brightness: g.brightness,
                  contrast: g.contrast,
                  saturation: g.saturation,
                  hue: g.hue,
                })),
            }
          : {}),
        segments: clips.map((c, i) => ({
          start: c.start,
          end: c.end,
          // Display name stays as typed; the file gets the safe version.
          label: normalizeFileStem(clipLabel(c, i)) || `clipe-${String(i + 1)}`,
          temp_path: videoById(project, c.videoId)?.tempPath ?? base.tempPath,
          speed: c.speed,
          // Source-clock instants; the backend splits the cut around them
          // and loops one extracted frame over silence for each.
          freezes: orderedFreezes(c).map((f) => ({ at: f.at, duration: f.duration })),
        })),
      },
      {
        onSuccess: ({ job_id }) => {
          setCutJobId(job_id)
        },
      },
    )
  }

  const cutStatus = cutJob.data?.status
  const cutFailed = cutStatus === 'failed' || cutStatus === 'cancelled'
  const exportState: ExportState = {
    // A job that was just created has no status yet — treat "queued but
    // unknown" as running so the buttons don't flicker back in between
    // the mutation resolving and the first poll landing.
    running: startCut.isPending || (cutJobId !== null && !cutFailed && cutStatus !== 'succeeded'),
    progress: cutJob.data?.progress ?? (startCut.isPending ? 2 : 0),
    message: cutJob.data?.message ?? (startCut.isPending ? 'Enviando...' : ''),
    error: cutFailed
      ? ((cutJob.data?.error?.['message'] as string | undefined) ??
        cutJob.data?.message ??
        'Falha ao exportar.')
      : startCut.isError
        ? errorMessage(startCut.error, 'Falha ao exportar.')
        : null,
    merged,
  }

  return { exportState, runExport }
}
